/**
 * CustomCamposFornecedor.tsx — o fornecedor da LISTA DA LOJA, com "Buscar no Tiny"
 * (migration 120, ficha #42).
 *
 * A lista de cada loja cresce conforme a necessidade (dele, 29/09: "não trazer
 * tudo, tem muita duplicata"). O campo:
 *   1. busca na lista da loja — o caso normal;
 *   2. "Não está na lista? Buscar no Tiny" — procura lá por nome ou CPF/CNPJ,
 *      com o documento e o código à vista (há três "Luiz Carlos Randon" sem
 *      CPF), e o escolhido ENTRA na lista;
 *   3. não existe no Tiny → "Sugerir" deixa só o nome; quem cadastra
 *      fornecedor é avisado, e a compra salva mas não vai ao Tiny até ligarem.
 *
 * O app nunca cria contato no Tiny.
 */
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { CustomCampoBusca, type ItemBusca } from './CustomCamposBusca';

export interface FornecedorLoja {
  id: number;
  contato_tiny: string | null;
  nome: string;
  cpf_cnpj: string | null;
  /** tiny | sugerido */
  cadastro: string;
}

interface ContatoTiny {
  contato_tiny: string;
  nome: string;
  fantasia: string | null;
  cpf_cnpj: string | null;
  situacao: string | null;
  na_lista: boolean;
}

const BASE = '/custos-pedido/ordens-compra/fornecedores';

function paraItem(f: FornecedorLoja): ItemBusca {
  return {
    id: f.id,
    rotulo: f.nome,
    detalhe:
      f.cadastro === 'sugerido'
        ? 'sugerido — ainda não está no Tiny'
        : [f.cpf_cnpj, f.contato_tiny ? `cód. ${f.contato_tiny}` : null].filter(Boolean).join(' · ') || null,
  };
}

/** O modal de busca no Tiny — também usado para LIGAR uma sugestão. */
export function CustomBuscaContatoTiny({
  lojaId,
  aberto,
  titulo,
  termoInicial = '',
  permiteSugerir = true,
  aoEscolher,
  aoSugerir,
  aoFechar,
}: {
  lojaId: number;
  aberto: boolean;
  titulo: string;
  termoInicial?: string;
  permiteSugerir?: boolean;
  aoEscolher: (c: ContatoTiny) => void;
  aoSugerir?: (nome: string) => void;
  aoFechar: () => void;
}) {
  const [termo, setTermo] = useState(termoInicial);
  const [itens, setItens] = useState<ContatoTiny[] | null>(null);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => {
    if (aberto) {
      setTermo(termoInicial);
      setItens(null);
    }
  }, [aberto, termoInicial]);

  async function buscar() {
    setBuscando(true);
    try {
      setItens(await api.get<ContatoTiny[]>(`${BASE}/tiny?loja_id=${lojaId}&termo=${encodeURIComponent(termo)}`));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível buscar no Tiny.');
      // Tiny fora do ar não pode travar quem lança: a lista vazia mostra o
      // "Sugerir" mesmo assim.
      setItens([]);
    } finally {
      setBuscando(false);
    }
  }

  return (
    <Modal className="casaa-modal" show={aberto} onHide={aoFechar} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="input-group mb-3">
          <div className="form-floating">
            <input
              id="busca-contato-tiny"
              className="form-control"
              placeholder=" "
              autoFocus
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && termo.trim().length >= 3) buscar();
              }}
            />
            <label htmlFor="busca-contato-tiny">Nome, CPF ou CNPJ</label>
          </div>
          <button type="button" className="btn btn-theme" onClick={buscar} disabled={buscando || termo.trim().length < 3}>
            {buscando ? <span className="spinner-border spinner-border-sm" /> : 'Buscar no Tiny'}
          </button>
        </div>

        {itens !== null && (
          <>
            {itens.length === 0 ? (
              <p className="text-secondary small mb-2">Nenhum contato com esse nome ou documento no Tiny desta loja.</p>
            ) : (
              <table className="table table-sm table-hover align-middle small mb-2">
                <thead>
                  <tr className="text-secondary text-uppercase">
                    <th>Nome</th>
                    <th>CPF / CNPJ</th>
                    <th>Código</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((c) => (
                    <tr key={c.contato_tiny}>
                      <td>
                        {c.nome}
                        {c.fantasia && <div className="text-secondary">{c.fantasia}</div>}
                      </td>
                      <td className="text-nowrap">{c.cpf_cnpj ?? <span className="text-secondary">sem documento</span>}</td>
                      <td className="text-secondary font-monospace">{c.contato_tiny}</td>
                      <td className="text-end text-nowrap">
                        <button type="button" className="btn btn-sm btn-outline-theme" onClick={() => aoEscolher(c)}>
                          {c.na_lista ? 'Já na lista — usar' : 'Usar este'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {permiteSugerir && aoSugerir && termo.trim().length >= 3 && (
              <div className="border-top pt-2 small">
                Não é nenhum destes?{' '}
                <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => aoSugerir(termo.trim())}>
                  Sugerir “{termo.trim()}” como fornecedor novo
                </button>
                <div className="text-secondary">
                  A compra salva, mas só vai ao Tiny depois que ele for cadastrado lá.
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export function CustomCampoFornecedorTiny({
  id,
  lojaId,
  valor,
  onChange,
  desabilitado,
}: {
  id: string;
  lojaId: number | null;
  /** o id da LINHA da lista da loja */
  valor: number | null;
  onChange: (fornecedorId: number | null) => void;
  desabilitado?: boolean;
}) {
  const [lista, setLista] = useState<FornecedorLoja[]>([]);
  const [buscaTiny, setBuscaTiny] = useState(false);

  async function carregar() {
    if (!lojaId) return;
    try {
      setLista(await api.get<FornecedorLoja[]>(`${BASE}?loja_id=${lojaId}`));
    } catch {
      setLista([]);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lojaId]);

  const escolhido = lista.find((f) => f.id === valor) ?? null;

  async function importar(c: ContatoTiny) {
    if (!lojaId) return;
    try {
      const f = await api.post<FornecedorLoja>(BASE, { loja_id: lojaId, contato_tiny: Number(c.contato_tiny) });
      await carregar();
      onChange(f.id);
      setBuscaTiny(false);
      toast.success(`${f.nome} entrou na lista da loja.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível importar.');
    }
  }

  async function sugerir(nome: string) {
    if (!lojaId) return;
    try {
      const f = await api.post<FornecedorLoja>(`${BASE}/sugestoes`, { loja_id: lojaId, nome });
      await carregar();
      onChange(f.id);
      setBuscaTiny(false);
      toast.success('Sugerido. Quem cadastra fornecedor foi avisado.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível sugerir.');
    }
  }

  return (
    <>
      <CustomCampoBusca
        id={id}
        label="Fornecedor"
        valor={escolhido ? paraItem(escolhido) : null}
        onChange={(item) => onChange(item ? item.id : null)}
        buscar={async (termo) => {
          const t = termo.trim().toLowerCase();
          return lista
            .filter((f) => !t || f.nome.toLowerCase().includes(t) || (f.cpf_cnpj ?? '').includes(t))
            .slice(0, 10)
            .map(paraItem);
        }}
        placeholder="Digite para achar na lista da loja"
        desabilitado={desabilitado || !lojaId}
        ajuda={
          <>
            {escolhido?.cadastro === 'sugerido' && (
              <span className="text-warning-emphasis me-2">
                <i className="bi bi-hourglass-split me-1"></i>Sugerido — ainda não está no Tiny.
              </span>
            )}
            {!desabilitado && (
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setBuscaTiny(true)}>
                Não está na lista? Buscar no Tiny
              </button>
            )}
          </>
        }
      />
      {lojaId && (
        <CustomBuscaContatoTiny
          lojaId={lojaId}
          aberto={buscaTiny}
          titulo="Buscar fornecedor no Tiny"
          aoEscolher={importar}
          aoSugerir={sugerir}
          aoFechar={() => setBuscaTiny(false)}
        />
      )}
    </>
  );
}
